import { useEffect, useMemo, useState } from "react";
import Countdown, { CountdownRendererFn } from "react-countdown";
import NextLink from "next/link";
import { format } from "date-fns";
import { RiUser3Fill } from "react-icons/ri";
import { SessionData } from "branch-sdk";
import type { Contributor } from "@memento/types/contributor";
import type { Card } from "@memento/types/card";

import { ThemeHeading } from "src/components/GroupVideoTheme/ThemeHeading";
import { ThemeButton } from "src/components/GroupVideoTheme/ThemeButton";
import { ThemeBodyText } from "src/components/GroupVideoTheme/ThemeBodyText";
import { ThemeBodyTextSecondary } from "src/components/GroupVideoTheme/ThemeBodyTextSecondary";
import { ThemeContentCutout } from "src/components/GroupVideoTheme/ThemeContentCutout";
import { ThemeSeparator } from "src/components/GroupVideoTheme/ThemeSeparator";
import HelpButton from "./HelpButton";
import constants from "src/constants";
import { getCardUrl, getIsDueDatePassed } from "src/libs/cardUtils";
import { isCardArchived } from "@memento/library/card";
import { ThemeBadge } from "./GroupVideoTheme/ThemeBadge";
import { NewlineBreak } from "./Elements/NewlineBreak";
import { CardThemeIdentifier } from "./GroupVideoTheme/card-theme-utils";
import { useCardThemed } from "./GroupVideoTheme/useCardThemed";
import { LoadingStatus } from "src/libs/fetcher";
import { useCardThemeIdentifier } from "./GroupVideoTheme/useCardThemeIdentifier";
import ReadMore from "./ReadMore";
import { useSubscription } from "src/hooks/useSubscription";
import { useAds } from "src/hooks/useAds";
import { isIOS } from "src/libs/browser-lib";
import { AppStoreButtonLabeled } from "src/components/AppStoreButton";
import { getBranchData, getDeepLink } from "src/libs/branch-lib";
import { cn } from "src/utils/cn";
import {
  useCardPublicSegments,
  type PublicSegmentsResponse,
} from "src/hooks/useCardPublicSegments";
import { getPluralEnding } from "@memento/library/text";

const FEATURE_IMAGE_SIZE = 200;
const FEATURE_IMAGE_OFFSET = -30;
const CUTOUT_PADDING = 5;
const CONTENT_PADDING = 20;

type CountdownItemProps = {
  themeId?: string | null;
  value: number;
  label: string;
};

// Remember, this updates about once every second. So if you see a bunch of
// re-renders, that's why.
function CountdownItem({ themeId, value, label }: CountdownItemProps) {
  return (
    <div className="text-center">
      <div className="font-bold">
        <ThemeBodyText themeId={themeId}>{value}</ThemeBodyText>
      </div>
      <div className="text-xs uppercase">
        <ThemeBodyTextSecondary themeId={themeId}>
          {label}
        </ThemeBodyTextSecondary>
      </div>
    </div>
  );
}

const countdownRenderer: (themeId?: string | null) => CountdownRendererFn =
  (themeId) =>
  ({ days, hours, minutes, seconds, completed }) => {
    const d = Math.round(days);
    const h = Math.round(hours);
    const m = Math.round(minutes);
    const s = Math.round(seconds);

    return completed ? (
      <ThemeBodyTextSecondary themeId={themeId}>
        <div>{"Due date passed"}</div>
      </ThemeBodyTextSecondary>
    ) : (
      <div>
        <div className="mx-auto flex max-w-[200px] justify-evenly gap-2">
          {d ? (
            <CountdownItem
              themeId={themeId}
              value={d}
              label={d > 1 ? "days" : "day"}
            />
          ) : null}
          <CountdownItem themeId={themeId} value={h} label="hrs" />
          <CountdownItem themeId={themeId} value={m} label="mins" />
          <CountdownItem themeId={themeId} value={s} label="secs" />
        </div>
      </div>
    );
  };

type GroupVideoSummaryButtonProps = {
  label?: string;
  onClick?: () => void;
  as?: "button" | "a";
  href?: string;
  variant: "primary" | "secondary";
  themeId?: CardThemeIdentifier;
  disabled?: boolean;
  "data-test"?: string;
};

function GroupVideoSummaryButton({
  label,
  onClick,
  as = "button",
  href,
  variant,
  themeId,
  disabled,
  ...otherButtonProps
}: GroupVideoSummaryButtonProps) {
  return (
    <ThemeButton
      as={as}
      {...(href ? { href } : {})}
      themeId={themeId}
      variant={variant}
      width="full"
      size="lg"
      onClick={onClick}
      disabled={disabled}
      {...otherButtonProps}
    >
      {label}
    </ThemeButton>
  );
}

export type GroupVideoSummaryProps = {
  card: Card;
  contributor?: Contributor | null;
  buttonPrimary?: Omit<GroupVideoSummaryButtonProps, "variant" | "themeId">;
  buttonSecondary?: Omit<GroupVideoSummaryButtonProps, "variant" | "themeId">;
  hasSubmitted?: boolean;
  useFallbackFeaturedImage?: boolean;
  isPreview?: boolean;
  allowContributionPastDueDate?: boolean;
  // when the caller already fetched this card's public segments (e.g. via
  // SSR), pass it through so we can skip a redundant client-side fetch
  initialPublicSegmentsData?: PublicSegmentsResponse;
};

export const GroupVideoSummary = ({
  card,
  contributor,
  buttonPrimary,
  buttonSecondary,
  hasSubmitted = false,
  useFallbackFeaturedImage = false,
  isPreview = false,
  allowContributionPastDueDate = false,
  initialPublicSegmentsData,
}: GroupVideoSummaryProps) => {
  const { subscription } = useSubscription();
  const { updateAds } = useAds();
  const [branchData, setBranchData] = useState<SessionData | null>(null);
  const [deepLinkUrl, setDeepLinkUrl] = useState<string | null>(null);

  useEffect(() => {
    updateAds(card, subscription);
  }, [card, subscription]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchBranchData = async () => {
      const data = await getBranchData();

      // No `data` because errors are handled in `getBranchData`
      if (!data) {
        return;
      }

      setBranchData(data);

      const url = await getDeepLink({
        campaign: "contributor-page",
        cardId: card?.cardId,
        cardUrl: card?.cardUrl,
        contributorId: contributor?.contributorId,
      });

      // No `url` because errors are handled in `getDeepLink`
      if (!url) {
        return;
      }

      setDeepLinkUrl(url);
    };

    // Fetch / initialize branch-sdk when
    // not in preview mode and
    // when user is on iOS
    if (isIOS() && !isPreview) {
      fetchBranchData();
    }
  }, [card, contributor, isPreview]);

  const countdownRendererMemoed = useMemo(
    () => countdownRenderer(card.themeId),
    [card.themeId]
  );

  const themeId = useCardThemeIdentifier(card);

  const { theme, status } = useCardThemed(themeId);

  const { totalSubmitted: submittedVideoCount } = useCardPublicSegments(
    card.cardUrl,
    card.cardId,
    initialPublicSegmentsData
  );

  if (status !== LoadingStatus.SUCCESS) {
    return null;
  }

  const shouldDisplayFeatureImage =
    useFallbackFeaturedImage ||
    Boolean(theme.featureImageUrl) ||
    Boolean(card.featureImageUrl);

  return (
    // This paddingTop accounts for the featured image being translated up half
    // it's height. Otherwise it would display over any components included
    // above this one.
    <div
      style={{
        paddingTop: shouldDisplayFeatureImage
          ? FEATURE_IMAGE_SIZE / 2 - FEATURE_IMAGE_OFFSET
          : undefined,
      }}
    >
      <div className="relative">
        {shouldDisplayFeatureImage ? (
          <div
            className="absolute left-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
            style={{
              top: FEATURE_IMAGE_OFFSET,
            }}
          >
            <div
              style={{
                width: FEATURE_IMAGE_SIZE,
                height: FEATURE_IMAGE_SIZE,
              }}
            >
              {card.featureImageUrl || theme.featureImageUrl ? (
                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                <img
                  src={card.featureImageUrl || theme.featureImageUrl}
                  alt="Group video featured image"
                  className="h-full w-full rounded-full object-cover"
                />
              ) : useFallbackFeaturedImage ? (
                <RiUser3Fill
                  size={84}
                  color="white"
                  className="h-full w-full rounded-full bg-gray-100 p-3"
                />
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="w-full overflow-auto rounded-2xl shadow-2xl backdrop-blur-sm">
          <ThemeContentCutout
            themeId={themeId}
            cutoutRadius={FEATURE_IMAGE_SIZE / 2 + CUTOUT_PADDING * 2}
            cutoutOffset={FEATURE_IMAGE_OFFSET}
            hideCutout={!shouldDisplayFeatureImage}
          >
            <div
              className={cn(
                `mx-auto flex max-w-sm flex-col items-center space-y-4 px-6 pb-8 text-center`,
                shouldDisplayFeatureImage ? undefined : `pt-8`
              )}
              style={{
                paddingTop: shouldDisplayFeatureImage
                  ? FEATURE_IMAGE_SIZE / 2 +
                    CUTOUT_PADDING +
                    CONTENT_PADDING +
                    FEATURE_IMAGE_OFFSET
                  : undefined,
              }}
            >
              {hasSubmitted ? (
                <div className="mx-auto my-2 inline-block">
                  <ThemeBadge themeId={themeId} variant="primary">
                    You added a video
                  </ThemeBadge>
                </div>
              ) : null}
              <h1 className="font-futura text-3xl">
                <ThemeHeading themeId={themeId}>{card.title}</ThemeHeading>
              </h1>
              <div className="text-base">
                <NewlineBreak>
                  <ThemeBodyText themeId={themeId}>
                    <ReadMore text={card.description} maxLines={6} />
                  </ThemeBodyText>
                </NewlineBreak>
              </div>
              <ThemeSeparator themeId={themeId} />
              {getIsDueDatePassed(card.dueDate) &&
              !allowContributionPastDueDate ? (
                <>
                  <p className="font-bold">
                    <ThemeBodyText themeId={themeId}>
                      Video submission due date has passed
                    </ThemeBodyText>
                  </p>
                  <div className="space-y-2 rounded-lg bg-gray-700 p-4 text-left text-sm text-white">
                    <p className="font-medium">
                      This Group Video's submissions are closed
                    </p>

                    <div>
                      <p>
                        Submissions closed on
                        {` ${format(new Date(card.dueDate), "MMM dd, yyyy")}`}.
                      </p>
                      <p>
                        The Group Video organizer can choose to extend the due
                        date.
                      </p>
                    </div>
                    <p>
                      When the Group Video is ready, the organizer decides who
                      to send it to. Please see{" "}
                      <HelpButton
                        variant="link"
                        articleId={
                          constants.helpScout.articleIds.viewMontageParticipant
                        }
                      >
                        How can I watch the Group Video?
                      </HelpButton>
                    </p>
                  </div>
                </>
              ) : isCardArchived(card) ? (
                <div className="space-y-2 rounded-lg bg-gray-700 p-4 text-left text-sm text-white">
                  <p className="font-medium">
                    This Group Video has been archived
                  </p>

                  <p>Submissions are no longer accepted.</p>
                </div>
              ) : (
                <div className="w-full space-y-4">
                  {submittedVideoCount > 0 ? (
                    <>
                      <p className="font-bold">
                        <ThemeBodyText themeId={themeId}>
                          {submittedVideoCount}{" "}
                          {getPluralEnding(
                            submittedVideoCount,
                            "people are",
                            "person is"
                          )}{" "}
                          already in the video!
                        </ThemeBodyText>
                      </p>
                      <ThemeSeparator themeId={themeId} />
                    </>
                  ) : null}
                  {getIsDueDatePassed(card.dueDate) ? (
                    <p className="font-bold">
                      <ThemeBodyText themeId={themeId}>
                        Video submission due date has passed
                      </ThemeBodyText>
                    </p>
                  ) : (
                    <>
                      <p className="font-bold">
                        <ThemeBodyText themeId={themeId}>
                          Video submissions are due in:
                        </ThemeBodyText>
                      </p>
                      <Countdown
                        date={card.dueDate}
                        renderer={countdownRendererMemoed}
                      />
                    </>
                  )}

                  {getIsDueDatePassed(card.dueDate) &&
                  allowContributionPastDueDate ? (
                    <div className="space-y-2 rounded-lg bg-gray-700 p-4 text-left text-sm text-white">
                      <p>
                        As the organizer or co-organizer, you may submit clips
                        after the due date has passed.
                      </p>

                      <p>
                        Other users are not able to submit clips after the due
                        date.
                      </p>

                      <p>
                        <NextLink href={getCardUrl(card.cardUrl, card.cardId)}>
                          Click here to change the due date.
                        </NextLink>
                      </p>
                    </div>
                  ) : null}

                  {buttonPrimary && deepLinkUrl ? (
                    <div className="w-full space-y-4">
                      <ThemeSeparator themeId={themeId} />
                      <AppStoreButtonLabeled
                        hasApp={Boolean(branchData?.has_app)}
                        deepLinkUrl={deepLinkUrl}
                        onPostTextClick={buttonPrimary.onClick}
                        postText="Continue in Browser"
                        preText="Get the free app to submit videos"
                        themeId={themeId}
                      />
                    </div>
                  ) : buttonPrimary || buttonSecondary ? (
                    <div className="w-full space-y-3">
                      {buttonPrimary ? (
                        <GroupVideoSummaryButton
                          themeId={themeId}
                          variant="primary"
                          {...buttonPrimary}
                        />
                      ) : null}
                      {buttonSecondary ? (
                        <GroupVideoSummaryButton
                          themeId={themeId}
                          variant="secondary"
                          {...buttonSecondary}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </ThemeContentCutout>
        </div>
      </div>
    </div>
  );
};
